import { Logger } from './logger.js'

/**
 * Fetches and parses a local JSON file with robust encoding detection.
 *
 * Handles files produced by CAD/BIM export pipelines that may be saved in
 * non-standard encodings. Detection order:
 *   1. UTF-16LE BOM (FF FE)
 *   2. UTF-16BE BOM (FE FF)
 *   3. UTF-8 BOM (EF BB BF)
 *   4. Heuristic: high ratio of null bytes in the first 100 bytes → likely UTF-16
 *   5. Fallback chain: tries all four encodings in sequence
 *
 * @param {string} url - URL of the JSON file to fetch.
 * @param {string} [errorPrefix='Error fetching file'] - Prefix used in error log messages.
 * @returns {Promise<object|null>} Parsed JSON object, or null if fetch or parsing fails.
 */
export async function fetchLocalJSON(url, errorPrefix = 'Error fetching file') {
  try {
    const response = await fetch(url)

    if (!response.ok) {
      Logger.error({
        message: `${errorPrefix} at ${url}: ${response.status} ${response.statusText}`,
      })
      return null
    }

    const buffer = await response.arrayBuffer()
    const uint8Array = new Uint8Array(buffer)

    // Detect encoding from BOM
    let encoding = 'utf-8'
    let bomOffset = 0

    if (uint8Array.length >= 2 && uint8Array[0] === 0xff && uint8Array[1] === 0xfe) {
      // UTF-16LE BOM
      encoding = 'utf-16le'
      bomOffset = 2
    } else if (uint8Array.length >= 2 && uint8Array[0] === 0xfe && uint8Array[1] === 0xff) {
      // UTF-16BE BOM
      encoding = 'utf-16be'
      bomOffset = 2
    } else if (
      uint8Array.length >= 3 &&
      uint8Array[0] === 0xef &&
      uint8Array[1] === 0xbb &&
      uint8Array[2] === 0xbf
    ) {
      // UTF-8 BOM
      encoding = 'utf-8'
      bomOffset = 3
    } else if (uint8Array.length > 10) {
      // Heuristic: count null bytes in the first 100 bytes
      let nullCount = 0
      for (let i = 0; i < Math.min(100, uint8Array.length); i++) {
        if (uint8Array[i] === 0) nullCount++
      }
      if (nullCount > Math.min(100, uint8Array.length) * 0.3) {
        // High null density → likely UTF-16 without BOM
        encoding = uint8Array[1] === 0 ? 'utf-16le' : 'utf-16be'
      }
    }

    // Try detected encoding first, then fall back through remaining options
    const encodingsToTry = [encoding, 'utf-8', 'utf-16le', 'utf-16be']
    let lastError = null

    for (const enc of encodingsToTry) {
      try {
        const decoder = new TextDecoder(enc)
        const slicedBuffer = bomOffset > 0 && enc === encoding ? buffer.slice(bomOffset) : buffer
        const text = decoder.decode(slicedBuffer)
        const parsed = JSON.parse(text)

        if (enc !== encoding) {
          Logger.warning({
            message: `Parsed "${url}" with fallback encoding: ${enc} (detected: ${encoding})`,
          })
        } else if (bomOffset > 0) {
          Logger.info({
            message: `Parsed "${url}" with detected encoding: ${enc} (BOM present)`,
          })
        }

        return parsed
      } catch (err) {
        lastError = err
        continue
      }
    }

    Logger.error({
      message: `${errorPrefix} at ${url}: failed to parse JSON (tried: ${encodingsToTry.join(', ')})`,
      content: lastError?.message,
    })
    return null
  } catch (err) {
    Logger.error({
      message: `Exception during fetch or parse of "${url}"`,
      content: err?.message,
    })
    return null
  }
}
